import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { Settings, ThemeType } from '../types';

interface SettingsContextType {
  settings: Settings;
  updateSettings: (updates: Partial<Settings>) => void;
  toggleSound: () => void;
  toggleCRT: () => void;
  setWallpaper: (wallpaper: string) => void;
  setTheme: (theme: ThemeType) => void;
}

const defaultSettings: Settings = {
  soundEnabled: true,
  crtEffect: false,
  wallpaper: '/wallpapers/bliss.jpg',
  theme: 'luna-blue',
};

const STORAGE_KEY = 'xp-portfolio-settings-v2';

const SettingsContext = createContext<SettingsContextType | null>(null);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(() => {
    // Load from localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...defaultSettings, ...JSON.parse(stored) };
      }
    } catch {
      // Ignore parsing errors
    }
    return defaultSettings;
  });

  // Save to localStorage whenever settings change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Ignore storage errors
    }
  }, [settings]);

  const updateSettings = (updates: Partial<Settings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  const toggleSound = () => {
    setSettings(prev => ({ ...prev, soundEnabled: !prev.soundEnabled }));
  };

  const toggleCRT = () => {
    setSettings(prev => ({ ...prev, crtEffect: !prev.crtEffect }));
  };

  const setWallpaper = (wallpaper: string) => {
    setSettings(prev => ({ ...prev, wallpaper }));
  };

  const setTheme = (theme: ThemeType) => {
    setSettings(prev => ({ ...prev, theme }));
  };

  // Apply theme class to document
  useEffect(() => {
    const root = document.documentElement;
    // Remove all theme classes
    root.classList.remove('theme-luna-blue', 'theme-luna-silver', 'theme-luna-olive');
    // Add current theme class
    root.classList.add(`theme-${settings.theme}`);
  }, [settings.theme]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        updateSettings,
        toggleSound,
        toggleCRT,
        setWallpaper,
        setTheme,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
